import React, { useState, useEffect, useRef } from 'react';
import {
  Typography, IconButton, TextField, Button, Box, Tooltip,
  CircularProgress, Divider, Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import LanguageIcon from '@mui/icons-material/Language';
import {
  getMonitoredDomains, addMonitoredDomain, deleteMonitoredDomain,
  getMonitoredDomainInfo, updateMonitoredDomain, getOverview,
} from '../services/api';
import useVisiblePoll from '../hooks/useVisiblePoll';

const fmtDate = (s) => {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  } catch { return null; }
};

const ago = (iso) => {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function DomainInfoTooltipBody({ info }) {
  if (!info) {
    return (
      <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 180 }}>
        <CircularProgress size={12} sx={{ color: 'rgba(232,228,248,0.5)' }} />
        <Typography sx={{ fontSize: '0.72rem' }}>Looking up RDAP + DNS…</Typography>
      </Box>
    );
  }
  if (info.error) {
    return <Typography sx={{ fontSize: '0.72rem' }}>Error: {info.error}</Typography>;
  }
  const Row = ({ label, value }) => (
    <Box display="flex" gap={1} sx={{ fontSize: '0.7rem', lineHeight: 1.5 }}>
      <Box sx={{ width: 78, color: 'rgba(232,228,248,0.5)', flexShrink: 0 }}>{label}</Box>
      <Box sx={{
        color: '#e8e4f8', fontFamily: 'monospace', wordBreak: 'break-all',
      }}>{value || <span style={{ opacity: 0.4 }}>n/a</span>}</Box>
    </Box>
  );
  const yesNo = (v) => (v === true ? 'yes' : v === false ? 'no' : null);
  const status = (info.rdap_status && info.rdap_status.length)
    ? info.rdap_status.slice(0, 3).join(', ') + (info.rdap_status.length > 3 ? '…' : '')
    : null;
  const ns = (info.nameservers && info.nameservers.length)
    ? info.nameservers.slice(0, 3).join(', ') + (info.nameservers.length > 3 ? '…' : '')
    : null;
  const mx = (info.mx_servers && info.mx_servers.length)
    ? info.mx_servers.slice(0, 2).join(', ') + (info.mx_servers.length > 2 ? '…' : '')
    : null;
  return (
    <Box sx={{ minWidth: 260, py: 0.4 }}>
      <Typography sx={{
        fontSize: '0.7rem', fontFamily: 'monospace', fontWeight: 700,
        color: '#a78bfa', mb: 0.6,
      }}>
        {info.domain_name}
      </Typography>
      {info.rdap_error
        ? <Typography sx={{ fontSize: '0.7rem', color: '#fbbf24' }}>RDAP: {info.rdap_error}</Typography>
        : (
          <>
            <Row label="Registrar" value={info.registrar} />
            <Row label="Registered" value={fmtDate(info.registration_date)} />
            <Row label="Status" value={status} />
            <Row label="Nameservers" value={ns} />
          </>
        )
      }
      <Divider sx={{ my: 0.6, borderColor: 'rgba(255,255,255,0.06)' }} />
      {info.dns_error
        ? <Typography sx={{ fontSize: '0.7rem', color: '#fbbf24' }}>DNS: {info.dns_error}</Typography>
        : (
          <>
            <Row label="MX" value={info.has_mx ? (mx || 'yes') : yesNo(info.has_mx)} />
            <Row label="SPF" value={yesNo(info.has_spf)} />
            <Row label="DMARC" value={yesNo(info.has_dmarc)} />
          </>
        )
      }
    </Box>
  );
}

/* Per-brand mini stats line: findings / high / clones with colored counts */
function BrandStats({ stats }) {
  if (!stats) {
    return (
      <Typography sx={{ fontSize: '0.64rem', color: 'rgba(232,228,248,0.2)' }}>
        no findings yet
      </Typography>
    );
  }
  const Item = ({ value, label, color }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.4 }}>
      <Box component="span" sx={{
        fontWeight: 800, color: value > 0 ? color : 'rgba(232,228,248,0.25)',
        fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </Box>
      <Box component="span" sx={{ color: 'rgba(232,228,248,0.32)', fontSize: '0.62rem' }}>
        {label}
      </Box>
    </Box>
  );
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.1, flexWrap: 'wrap' }}>
      <Item value={stats.total} label="found" color="#e8e4f8" />
      <Item value={stats.high} label="high" color="#fb923c" />
      <Item value={stats.clones} label="clones" color="#f87171" />
      {stats.last_detection && (
        <Typography component="span" sx={{ fontSize: '0.62rem', color: 'rgba(232,228,248,0.25)' }}>
          · last {ago(stats.last_detection)}
        </Typography>
      )}
    </Box>
  );
}

function MonitoredDomains({ onRefresh, showNotification, refreshTrigger }) {
  const [domains, setDomains] = useState([]);
  const [brandStats, setBrandStats] = useState({});   // domain_name -> by_brand card
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  // Per-domain RDAP/DNS info, keyed by id. Value is null while in-flight,
  // or the payload (or {error:'...'}) once resolved.
  const [domainInfo, setDomainInfo] = useState({});
  const inflightRef = useRef(new Set());

  const loadDomains = async () => {
    try {
      const response = await getMonitoredDomains();
      setDomains(response.data);
    } catch {
      showNotification('Error loading monitored domains', 'error');
    }
  };

  const loadBrandStats = async () => {
    try {
      const { data } = await getOverview();
      const map = {};
      (data.by_brand || []).forEach(card => { map[card.brand] = card; });
      setBrandStats(map);
    } catch { /* stats are decorative; the list still works without them */ }
  };

  const fetchDomainInfo = async (id) => {
    // a cached error is retryable; a cached result or an in-flight lookup is not
    if ((domainInfo[id] && !domainInfo[id].error) || inflightRef.current.has(id)) return;
    inflightRef.current.add(id);
    setDomainInfo((prev) => ({ ...prev, [id]: null }));
    try {
      const { data } = await getMonitoredDomainInfo(id);
      setDomainInfo((prev) => ({ ...prev, [id]: data }));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'lookup failed';
      setDomainInfo((prev) => ({ ...prev, [id]: { error: msg } }));
    } finally {
      inflightRef.current.delete(id);
    }
  };

  useEffect(() => {
    loadDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);
  useVisiblePoll(loadBrandStats, 30000, [refreshTrigger]);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      showNotification('Please enter a domain name', 'warning');
      return;
    }
    setLoading(true);
    try {
      await addMonitoredDomain(newDomain.trim().toLowerCase());
      setNewDomain('');
      setShowAdd(false);
      await loadDomains();
      onRefresh();
      showNotification(`Domain "${newDomain}" added successfully`, 'success');
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Error adding domain', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (domain) => {
    const next = !domain.enabled;
    // Optimistic flip; revert on failure
    setDomains(ds => ds.map(d => d.id === domain.id ? { ...d, enabled: next } : d));
    try {
      await updateMonitoredDomain(domain.id, { enabled: next });
      showNotification(
        next ? `Monitoring resumed for ${domain.domain_name}`
             : `Monitoring paused for ${domain.domain_name} (findings kept)`,
        'success');
      onRefresh();
    } catch {
      setDomains(ds => ds.map(d => d.id === domain.id ? { ...d, enabled: !next } : d));
      showNotification('Error updating domain', 'error');
    }
  };

  const handleDeleteDomain = async (id, domainName) => {
    if (!window.confirm(`Remove "${domainName}" from monitoring?`)) return;
    const purge = window.confirm(
      `Also delete all historical findings for "${domainName}"?\n\nOK = delete findings\nCancel = keep them`
    );
    try {
      await deleteMonitoredDomain(id, purge);
      await loadDomains();
      onRefresh();
      showNotification(
        purge
          ? `Domain "${domainName}" and its findings removed`
          : `Domain "${domainName}" removed, findings preserved`,
        'success'
      );
    } catch {
      showNotification('Error removing domain', 'error');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box display="flex" alignItems="center" gap={0.8}>
          <LanguageIcon sx={{ fontSize: 16, color: 'rgba(232,228,248,0.35)' }} />
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: 0.7, color: 'rgba(232,228,248,0.35)',
          }}>
            Monitored Domains
          </Typography>
        </Box>
        <Box sx={{
          px: 0.8, py: 0.1, borderRadius: '5px', bgcolor: 'rgba(255,255,255,0.08)',
          fontSize: '0.65rem', fontWeight: 700, color: 'rgba(232,228,248,0.5)',
        }}>
          {domains.length}
        </Box>
      </Box>

      {/* Brand cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {domains.length === 0 ? (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(232,228,248,0.2)', fontSize: '0.75rem' }}>
              No domains monitored
            </Typography>
          </Box>
        ) : (
          domains.map((domain) => (
            <Tooltip
              key={domain.id}
              arrow placement="right"
              enterDelay={450} enterNextDelay={300} leaveDelay={100}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(20,18,32,0.97)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    px: 1.2, py: 1, maxWidth: 360,
                  },
                },
                arrow: { sx: { color: 'rgba(20,18,32,0.97)' } },
              }}
              onOpen={() => fetchDomainInfo(domain.id)}
              title={<DomainInfoTooltipBody info={domainInfo[domain.id]} />}
            >
              <Box sx={{
                p: 1.2, borderRadius: 2, position: 'relative',
                bgcolor: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                opacity: domain.enabled ? 1 : 0.55,
                transition: 'border-color 0.15s, background 0.15s',
                '&:hover': {
                  borderColor: 'rgba(167,139,250,0.25)',
                  bgcolor: 'rgba(167,139,250,0.04)',
                },
                '&:hover .card-actions': { opacity: 1 },
              }}>
                <Box display="flex" alignItems="center" gap={0.8}>
                  <Box sx={{
                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    bgcolor: domain.enabled ? '#4ade80' : 'rgba(255,255,255,0.18)',
                    boxShadow: domain.enabled ? '0 0 6px rgba(74,222,128,0.5)' : 'none',
                  }} />
                  <Typography sx={{
                    color: '#e8e4f8', fontFamily: 'monospace', fontSize: '0.78rem',
                    fontWeight: 600, flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {domain.domain_name}
                  </Typography>
                  <Box className="card-actions" sx={{
                    display: 'flex', alignItems: 'center', gap: 0,
                    opacity: { xs: 1, md: 0 }, transition: 'opacity 0.15s',
                  }}>
                    <Tooltip title={domain.enabled ? 'Pause monitoring' : 'Resume monitoring'} placement="top">
                      <Switch
                        size="small"
                        checked={domain.enabled}
                        onChange={() => handleToggleEnabled(domain)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          transform: 'scale(0.7)', mr: -0.5,
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#4ade80' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'rgba(74,222,128,0.35)' },
                        }}
                      />
                    </Tooltip>
                    <IconButton
                      aria-label={`Remove ${domain.domain_name}`}
                      onClick={() => handleDeleteDomain(domain.id, domain.domain_name)}
                      size="small"
                      sx={{
                        color: 'rgba(255,255,255,0.2)', p: 0.4,
                        '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,0.08)' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ mt: 0.6, ml: '15px' }}>
                  <BrandStats stats={brandStats[domain.domain_name]} />
                </Box>
              </Box>
            </Tooltip>
          ))
        )}
      </Box>

      {/* Add domain */}
      {showAdd ? (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2,
                   border: '1px solid rgba(255,255,255,0.07)' }}>
          <TextField
            fullWidth size="small" placeholder="example.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
            disabled={loading} autoFocus
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': { fontSize: '0.82rem' },
            }}
          />
          <Box display="flex" gap={1}>
            <Button fullWidth size="small" variant="contained" onClick={handleAddDomain}
              disabled={loading} sx={{ py: 0.5, fontSize: '0.75rem' }}>
              {loading ? 'Adding...' : 'Add'}
            </Button>
            <Button fullWidth size="small" onClick={() => { setShowAdd(false); setNewDomain(''); }}
              sx={{ py: 0.5, fontSize: '0.75rem', color: 'rgba(232,228,248,0.4)' }}>
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Button
          fullWidth size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setShowAdd(true)}
          sx={{
            mt: 1.2, py: 0.6, fontSize: '0.72rem', color: 'rgba(232,228,248,0.35)',
            border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 1.5,
            '&:hover': { color: '#a78bfa', borderColor: 'rgba(167,139,250,0.3)',
                         bgcolor: 'rgba(167,139,250,0.05)' },
          }}
        >
          Add Domain
        </Button>
      )}
    </Box>
  );
}

export default MonitoredDomains;
